<template>
  <p>
    <list-format-display v-if="displayComponents.href"
                         :is-list="displayAsList"
                         :list-value="displayComponents.value"
                         :value="displayComponents.displayValue"
                         tag="a"
                         v-bind="linkAttrs"
    />
    <list-format-display v-else
                         :is-list="displayAsList"
                         :list-value="displayComponents.value"
                         :value="displayComponents.displayValue"
                         tag="span"
    />

    <button
      v-if="propertyHasSecurityMaskToggle"
      @click="toggleSecurityMask"
      class="btn-icon"
      :class="securityMaskActive ? 'light-grey' : 'grey'"
    >
      <span class="sr-only">{{ securityMaskActive ? "Show" : "Hide" }} masked data</span>
      <icon :class="securityMaskActive ? 'hide' : 'show'"></icon>
    </button>
  </p>
</template>

<script lang="ts">
import Vue from "vue";
import { ObjectModelPropertyRootType } from "~/enums/objectModelPropertyType";
import { RouteHelper } from "~/helpers/routeHelper";
import { Route } from "~/enums/route";
import { ObjectModelBuiltinPropertyType } from "~/enums/objectModelBuiltinPropertyType";
import ObjectModelPropertyValue from "~/models/objectModelProperties/objectModelPropertyValue";
import ContentElementComponent from "./content-element-component.vue";
import ObjectModelProperty from "~/models/objectModelProperties/objectModelProperty";
import { ObjectModelPropertyCapability } from "~/models/objectModelProperties/objectModelPropertyCapability";
import selectedMixin from "../mixins/selectedMixin";
import SecurityMaskableViewer from "~/models/securityMaskableViewer";
import {NumberHelper} from "~/helpers/numberHelper"
import {InputFormatType} from '../../form_builder/models/inputFormatType'
import {StringHelper} from "~/helpers/stringHelper"
import {ListFormatType} from '~/enums/listFormatType'
import ListFormatDisplay from '~/components/content/list-format-display.vue'

export default Vue.extend({
  components: {
    ListFormatDisplay,
  },
  extends: ContentElementComponent,
  mixins: [selectedMixin],
  data: function () {
    return {
      securityMaskActive: true,
    };
  },
  computed: {
    propertyIsCurrency(): boolean {
      return (
        this.modelProperty.property_type === ObjectModelPropertyRootType.Number && this.modelProperty.format_type === InputFormatType.Currency
      );
    },
    propertySecurityMaskEnabled(): boolean {
      return this.modelProperty && this.modelProperty.is_security_maskable_enabled;
    },
    propertyHasSecurityMaskToggle(): boolean {
      return (
        this.propertySecurityMaskEnabled &&
        this.modelProperty.securityMaskingFullViewersArray.some((x: SecurityMaskableViewer<ObjectModelProperty>) => {
          return x.matches(this.selectedUser, this.entry);
        })
      );
    },
    displayComponents() {
      let displayValue;
      let decimalPlaces;
      let href = null;
      let newTab = true;
      let value = null

      if (this.modelProperty) {
        value = this.selectedObjectModelEntry.valueFor(this.modelProperty.client_id);
        displayValue = this.selectedObjectModelEntry.displayValueFor(this.modelProperty.client_id);
        decimalPlaces = this.modelProperty.decimal_places || 0;

        if (this.contentElement.propertyIsUrlFormat) {
          href = value;
        } else if (this.contentElement.propertyIsDateTime) {
          displayValue = ObjectModelPropertyValue.parseDate(value, this.contentElement, this.modelProperty);
        } else if (this.modelProperty.property_type === ObjectModelPropertyRootType.MappedProperty) {
          if (this.isPreview) {
            if (this.contentElement.mappedPropertyLinkedDetailViewsArray.length > 0) {
              href = "javascript:void(0);";
              newTab = false;
            }
          } else {
            const linked_property_client_id = this.selectedObjectModelEntry.valueFor(
              this.modelProperty.client_id + "_linked_relation_mapped_property"
            );
            const linked_entry_id = this.selectedObjectModelEntry.valueFor(this.modelProperty.client_id + "_entry_id");
            const selectedMappedPropertyLinkedDetailView =
              this.contentElement.mappedPropertyLinkedDetailViewsArray.find(
                (mappedPropertyLinkedDetailView) =>
                  mappedPropertyLinkedDetailView.property_client_id === linked_property_client_id
              );

            if (selectedMappedPropertyLinkedDetailView) {
              let from = { from: "Reporting & Submissions Dashboard" };
              const query = {};

              query["bc"] = [
                { t: "om", v: "bv" },
                { t: "dv", v: { dv: this.selectedDetailView.detail_view_id, e: this.selectedObjectModelEntry.id } },
                { t: "dv", v: { dv: selectedMappedPropertyLinkedDetailView.detail_view_id, e: linked_entry_id } },
              ];

              href = RouteHelper.path({
                route: Route.detailView,
                namedWildcardOptions: [
                  { wildcard: ":id", modelOrId: selectedMappedPropertyLinkedDetailView.detail_view_id },
                  { wildcard: ":entry_id", modelOrId: linked_entry_id },
                ],
                query: query,
              });
            } else {
              if (selectedMappedPropertyLinkedDetailView) {
                let query = { from: "Reporting & Submissions Dashboard" };
                href = RouteHelper.path({
                  route: Route.detailView,
                  namedWildcardOptions: [
                    { wildcard: ":id", modelOrId: selectedMappedPropertyLinkedDetailView.detail_view_id },
                    { wildcard: ":entry_id", modelOrId: linked_entry_id },
                  ],
                  query: query,
                });
              }
            }
          }
        }

        if (this.propertySecurityMaskEnabled) {
          if (this.propertyHasSecurityMaskToggle) {
            displayValue = this.securityMaskActive ? displayValue.masked : displayValue.unmasked;
          } else {
            displayValue = displayValue.masked;
          }
          return { value, displayValue, href, newTab };
        }
      } else if (this.contentElement.builtinPropertyType) {
        let baseVal = this.selectedObjectModelEntry.valueForBuiltin(this.contentElement.builtinPropertyType);
        displayValue = baseVal;

        if (
          this.contentElement.builtinPropertyType === ObjectModelBuiltinPropertyType.CreatedAt ||
          this.contentElement.builtinPropertyType === ObjectModelBuiltinPropertyType.UpdatedAt
        ) {
          displayValue = ObjectModelPropertyValue.parseDate(baseVal, this.contentElement, this.modelProperty);
        }
      }

      if (!displayValue && this.selectedObjectModelEntry?.view_data?.content_element_data) {
        const contentElementData = this.selectedObjectModelEntry.view_data.content_element_data;
        if (contentElementData[this.contentElement.client_id]) {
          displayValue = contentElementData[this.contentElement.client_id];
        }
      }

      if (this.contentElement.format_type === InputFormatType.Currency && this.isNumber(displayValue)) {
        displayValue = NumberHelper.numberToCurrency("en-us", "USD", displayValue)
      }

      if (this.contentElement.format_type === InputFormatType.Percentage && this.isNumber(displayValue)) {
        const displayDecimalPlaces = Math.max(decimalPlaces - 2, 0);
        displayValue = (parseFloat(displayValue) * 100).toFixed(displayDecimalPlaces) + '%'
      }

      if (this.displayAsList) {
        value = this.entry.valueFor(this.modelProperty.client_id).map(this.modelProperty.getLabel.bind(this.modelProperty))
      }

      if (displayValue === null || displayValue === undefined || displayValue === "") {
        displayValue = this.contentElement.placeholder_text;
      }


      return { value, displayValue, href, newTab };
    },
    linkAttrs() {
      let attrs: any = {
        href: this.displayComponents.href,
      };

      if (this.displayComponents.newTab) {
        attrs.target = "_blank";
      }

      return attrs;
    },
    modelProperty() {
      return this.contentElement.property
    },
    isPreview(): boolean {
      return this.$route.name === "detailViewPreview";
    },
    displayAsList(): boolean {
      return this.contentElement.list_format_type === ListFormatType.NewLine &&
        this.modelProperty &&
        this.modelProperty.isPickList &&
        this.modelProperty.isDataCollection
    },
  },
  methods: {
    toggleSecurityMask(): void {
      this.securityMaskActive = !this.securityMaskActive;
    },
    isNumber(val: unknown): boolean {
      return typeof val === 'string' ? StringHelper.isNumber(val) : NumberHelper.isNumber(val)
    },
  },
});
</script>